import { type ChatMessageType, type ChatSession } from "@/api/chat";
import { ChatSessionHeaderActions } from "@/components/chats/chat-session-header-actions";
import { Button } from "@/components/ui/button";
import { ButtonLoading } from "@/components/ui/button-loading";
import { ResizableTextarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useAppContext } from "@/providers/app-context";
import { useChatMessagesContext } from "@/providers/chat-message-provider";
import { useProductName } from "@/lib/branding";
import { useMutation } from "@tanstack/react-query";
import { BotIcon, BotOff, ChevronsRight, SendIcon } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { PdfModal } from "../doc-viewer/components/PdfModal";
import ChatMessage from "./chat-message";
import { useTypingDetection } from "@/lib/utils";
import { useUser } from "@/api/user";
import { useAICapStatus } from "@/api/organizations";

const ChatSessionDetail = ({
  chatSession,
  embedded = false,
}: {
  chatSession: ChatSession;
  embedded?: boolean;
}) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [inputMessage, setInputMessage] = React.useState("");
  const formRef = React.useRef<HTMLFormElement>(null);
  const [inputKey, setInputKey] = React.useState(0);
  const [initialScrollDone, setInitialScrollDone] = React.useState(false);
  const navigate = useNavigate();
  const { setCurrentChatSessionId } = useAppContext();
  const {
    costCapReached: aiCapReached,
    message: costCapStatusMessage,
  } = useAICapStatus();
  const productName = useProductName();

  const { toast } = useToast();
  const [pdfViewerDoc, setPdfViewerDoc] = React.useState<{
    id: string;
    filename: string;
  }>();
  const [generateAiResponse, setGenerateAiResponse] = React.useState(true);
  const [isTyping, resetTyping] = useTypingDetection(inputMessage, 2000);
  const currentUser = useUser();

  React.useEffect(() => {
    setCurrentChatSessionId(chatSession.id);
    return () => {
      setCurrentChatSessionId(undefined);
    };
  }, [chatSession]);

  const { messages, sendMessage, canSendMessages } = useChatMessagesContext();

  React.useEffect(() => {
    // const { offsetHeight, scrollHeight, scrollTop } =
    //   scrollRef.current as HTMLDivElement;
    setInputKey((prev) => prev + 1);
    setInitialScrollDone(false);
  }, [chatSession]);

  React.useEffect(() => {
    // scroll to the bottom everytime we get a new message
    if (!scrollRef.current) return;
    const { offsetHeight, scrollHeight, scrollTop } =
      scrollRef.current as HTMLDivElement;

    if (!initialScrollDone && messages && messages.length > 0) {
      scrollRef.current?.scrollTo({
        top: scrollHeight,
        left: 0,
      });
      setInitialScrollDone(true);
    }

    if (scrollHeight <= scrollTop + offsetHeight + 400) {
      scrollRef.current?.scrollTo({
        top: scrollHeight,
        left: 0,
        behavior: "smooth",
      });
    } else {
      // show indicator of new message
    }
  }, [messages]);

  const mutationFn = React.useCallback(
    async (message: string) => {
      // Don't generate AI response if cost cap is exceeded, even if toggle is on
      const shouldGenerateAI = generateAiResponse && !aiCapReached;
      sendMessage?.(message, shouldGenerateAI);
    },
    [generateAiResponse, aiCapReached, sendMessage],
  );

  const mutation = useMutation({
    mutationFn,
  });

  // Handle the form submission for sending a new message
  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSendMessages) return;

    if (inputMessage.trim() === "") return;
    mutation.mutate(inputMessage);
    resetTyping();
    setInputMessage("");
  };

  const handleChange = (event: any) => {
    if (event.type === "keyup" && event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      formRef.current?.requestSubmit();
      return;
    } else if (
      event.type === "keyup" &&
      event.key === "Enter" &&
      event.shiftKey
    ) {
      setInputMessage(event.target.value);
      return;
    } else if (event.target.value.at(-1) === "\n") {
      if (inputMessage !== "") setInputMessage(event.target.value);
    } else {
      setInputMessage(event.target.value);
    }
  };

  const { submitEvent } = useChatMessagesContext();

  React.useEffect(() => {
    if (!currentUser.data?.id) return;

    const sendTypingEvent = () => {
      submitEvent({
        user: {
          id: currentUser.data?.id || "",
          first_name: currentUser.data?.first_name || "",
          last_name: currentUser.data?.last_name || "",
        },
        events: {
          is_typing: isTyping,
        },
      });
    };

    sendTypingEvent();

    // If user is typing, set up heartbeat to keep sending typing events
    let heartbeatInterval: NodeJS.Timeout | null = null;
    if (isTyping) {
      heartbeatInterval = setInterval(sendTypingEvent, 8000);
    }

    return () => {
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
    };
  }, [isTyping, currentUser.data, submitEvent]);

  return (
    <div
      className={cn(
        "relative flex w-full flex-col gap-y-2 rounded-lg bg-card text-card-foreground",
        embedded ? "flex-1" : "h-[calc(100vh-61px-64px-24px-16px)]",
      )}
    >
      {!embedded && (
        <div className="flex w-full flex-row items-start justify-between gap-x-2 bg-white p-2 pt-0">
          <div
            className="w-fit rounded-full border border-black p-2 hover:cursor-pointer"
            onClick={() => navigate("/chats")}
          >
            <ChevronsRight className="h-5 w-5 stroke-[1.5]" />
          </div>
          <div className="flex w-full flex-row justify-between gap-x-2 rounded-md border bg-white p-2">
            <div className="flex-1">
              <div className="line-clamp-1 text-sm hover:cursor-pointer hover:underline">
                {chatSession.title}
              </div>
            </div>
            <ChatSessionHeaderActions
              chatSession={chatSession}
              onDeleted={() => navigate("/chats")}
            />
          </div>
        </div>
      )}
      <div
        ref={scrollRef}
        className="flex h-full max-h-full flex-col overflow-y-auto pb-6 pr-2 pt-0 scrollbar-thin scrollbar-thumb-neutral-300"
      >
        <div className="mt-auto space-y-8 px-2">
          {messages?.map((message: ChatMessageType, index: number) => {
            return (
              <ChatMessage
                key={index}
                message={message}
                setPdfViewerDoc={setPdfViewerDoc}
              />
            );
          })}
        </div>
      </div>
      <div className="relative mx-6">
        <IsTypingComponent />
      </div>
      <div className="flex flex-col gap-2 px-2 pt-0" key={inputKey}>
        <form
          className="flex w-full items-center space-x-2"
          onSubmit={onSubmit}
          ref={formRef}
        >
          <ResizableTextarea
            autoFocus
            id="message"
            // placeholder="Type your message..."
            autoComplete="off"
            value={inputMessage}
            onKeyUp={handleChange}
            onChange={handleChange}
          />
          <ButtonLoading
            size="icon"
            type="submit"
            disabled={!inputMessage || !canSendMessages}
            isLoading={mutation.isPending}
            className="bg-cf-foreground hover:bg-cf-foreground/80"
            title={aiCapReached ? costCapStatusMessage : undefined}
          >
            <SendIcon className="h-4 w-4" />
            <span className="sr-only">Send</span>
          </ButtonLoading>
        </form>
      </div>
      <div className="pl-8">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className={aiCapReached ? "cursor-not-allowed" : ""}>
                <Button
                  variant="outline"
                  className={`rounded-full ${generateAiResponse && !aiCapReached ? "bg-cf-foreground/30 hover:bg-cf-foreground/50" : ""} ${aiCapReached ? "cursor-not-allowed" : ""}`}
                  onClick={() => {
                    if (aiCapReached) {
                      toast({
                        title: "AI Limit Reached",
                        description:
                          costCapStatusMessage ||
                          "AI functionality is disabled due to organization limits",
                        variant: "destructive",
                      });
                      return;
                    }
                    setGenerateAiResponse((prev) => !prev);
                  }}
                  disabled={aiCapReached}
                >
                  {aiCapReached ? (
                    <BotOff className="pr-2" />
                  ) : (
                    <BotIcon className="pr-2" />
                  )}
                  {productName}
                </Button>
              </span>
            </TooltipTrigger>
            {aiCapReached && (
              <TooltipContent>
                {costCapStatusMessage ||
                  "AI functionality is disabled due to organization limits"}
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>
      {pdfViewerDoc && (
        <PdfModal
          doc={pdfViewerDoc}
          target={undefined}
          onClose={() => setPdfViewerDoc(undefined)}
          readOnly // Disable Q&A and highlighting in chat context
        />
      )}
    </div>
  );
};

const IsTypingComponent = () => {
  const { usersTyping } = useChatMessagesContext();
  const [aiUsersTyping, setAiUsersTyping] = React.useState<
    {
      first_name: string;
      last_name: string;
      id: string;
    }[]
  >([]);
  const [humanUsersTyping, setHumanUsersTyping] = React.useState<
    {
      first_name: string;
      last_name: string;
      id: string;
    }[]
  >([]);

  React.useEffect(() => {
    setAiUsersTyping(
      usersTyping.filter((u) => u.id === null && u.first_name === "ForgeIQ"),
    );
    setHumanUsersTyping(usersTyping.filter((u) => u.id !== null));
  }, [usersTyping]);

  const bouncingDots = (
    <div>
      <div className="flex w-fit flex-row p-1 text-sm">
        <div className="animate-bounce delay-100 duration-1000 ease-in-out">
          ·
        </div>
        <div className="animate-bounce delay-150 duration-1000 ease-in-out">
          ·
        </div>
        <div className="animate-bounce delay-200 duration-1000 ease-in-out">
          ·
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex gap-x-2">
      {/* Human users typing indicator */}
      {humanUsersTyping.length > 0 && (
        <div className="flex h-8 w-fit items-center gap-x-1 rounded-lg bg-muted px-2 text-xs text-muted-foreground">
          <span>
            {humanUsersTyping
              .map((u) => `${u.first_name} ${u.last_name}`)
              .join(", ")}
          </span>
          {bouncingDots}
        </div>
      )}

      {/* AI users typing indicator */}
      {aiUsersTyping.length > 0 && (
        <div className="flex h-8 w-fit items-center gap-x-1 rounded-lg bg-cf-foreground px-2 text-xs text-white/90">
          <BotIcon className="h-4 w-4 text-white" />
          <span>
            {aiUsersTyping
              .map((u) => `${u.first_name} ${u.last_name}`)
              .join(", ")}
          </span>
          {bouncingDots}
        </div>
      )}
    </div>
  );
};

export default ChatSessionDetail;
